import React, { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Field, useField, useForm } from 'react-final-form'
import { IntlShape, MessageDescriptor, useIntl } from 'react-intl'
import { KeyboardType, Pressable, View } from 'react-native'

import { JSONSchema7TypeName } from 'json-schema'
import { capitalize, get, isArray, isEmpty, isFunction, isNil, isNumber, isObject, isString } from 'lodash-es'
import styled, { useTheme } from 'styled-components/native'

import { formatFunctions, parseFunctions } from '@lyrahealth-inc/shared-app-logic/src/features/assignments/utils'
import {
  thisFieldIsRequiredErrorMessage,
  unsupportedCountryCodeErrorMessage,
} from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/messages'
import { parseDateString } from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/dateUtils'
import { characterLimit } from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/validationUtils'
import {
  CheckboxButtonType,
  FieldSchema,
  RadioButtonType,
} from '@lyrahealth-inc/shared-app-logic/src/models/formMetadata/FormMetadata'

import { BodyText } from '../../atoms/bodyText/BodyText'
import { ButtonRangeGroupRFF } from '../../atoms/formElements/ButtonRangeGroup'
import { CheckboxRFF } from '../../atoms/formElements/Checkbox'
import { CheckboxGroupRFF } from '../../atoms/formElements/CheckboxGroup'
import { DateFieldRFF } from '../../atoms/formElements/DateField'
import { DatePickerSelectFieldRFF } from '../../atoms/formElements/DatePickerSelectField'
import { EditableInputField, EditableInputFieldProps } from '../../atoms/formElements/EditableInputField'
import { FileUploadRFF } from '../../atoms/formElements/FileUpload'
import { ImageCheckboxGroupRFF } from '../../atoms/formElements/ImageCheckboxGroup'
import { ImageRadioGroupRFF } from '../../atoms/formElements/ImageRadioGroup'
import { InputFieldProps, InputFieldRFF } from '../../atoms/formElements/InputField'
import { MaskedInputRFF } from '../../atoms/formElements/MaskedInput'
import { MulticolorSliderFieldRFF } from '../../atoms/formElements/MulticolorSliderField'
import { MultiSelectRFF } from '../../atoms/formElements/MultiSelect'
import { PasswordFieldRFF } from '../../atoms/formElements/passwordField/PasswordField'
import { RadioGroupRFF } from '../../atoms/formElements/RadioButtons'
import { SelectFieldRFF } from '../../atoms/formElements/SelectField'
import { SliderFieldRFF } from '../../atoms/formElements/SliderField'
import { TimeFieldRFF } from '../../atoms/formElements/TimeField'
import { TypeAheadRFF } from '../../atoms/formElements/typeAhead/TypeAhead'
import {
  TypeAheadCustomOptionsConfig,
  ValueFetchMode,
} from '../../atoms/formElements/typeAhead/TypeAheadCustomOptionsConfig'
import { AddIcon } from '../../atoms/icons/AddIcon'
import { ChevronIcon, ChevronIconDirection } from '../../atoms/icons/ChevronIcon'
import * as Illustrations from '../../atoms/illustrations/index'
import { DbtUnderageErrorBanner } from '../../customers/registration/dbt/DbtUnderageErrorBanner'
import { FeedbackStarsRFF } from '../../molecules/feedbackStars/FeedbackStars'
import { GenAiHeader } from '../../molecules/genAiHeader/GenAiHeader'
import { InlineErrorBanner } from '../../molecules/inlineErrorBanner/InlineErrorBanner'
import { InlineInfoBanner } from '../../molecules/inlineInfoBanner/InlineInfoBanner'
import { InlineSuccessBanner } from '../../molecules/inlineSuccessBanner/InlineSuccessBanner'
import { InlineWarningBanner } from '../../molecules/inlineWarningBanner/InlineWarningBanner'
import { RequiredCheckboxWithBanner } from '../../molecules/requiredCheckboxWithBanner/RequiredCheckboxWithBanner'
import { UserContactInformationCard } from '../../molecules/userContactInformationCard/UserContactInformationCard'
import { WordCloudRFF } from '../../molecules/wordCloud/WordCloud'
import { CountrySelectorRFF } from '../../organisms/countrySelector/CountrySelector'
import { InternationalPhoneNumberEntryRFF } from '../../organisms/internationalPhoneNumberEntry/InternationalPhoneNumberEntry'
import { BodyTextSize } from '../../styles/typeStyles'
import { AnimatedSharedValues, AnimatedWrapper } from '../../templates/animatedWrapper/AnimatedWrapper'
import { HealthPlanSelectorRFF } from '../healthPlanSelector/HealthPlanSelector'

import type { AddIconWidgetProps, RequiredCheckboxWithBannerWidgetProps, UICoreWidgetProps } from './types'

/**
 * List of widgets to be used be React Json Schema Form
 */

type CheckboxSubTextFieldProps = {
  name: string
  placeholder?: string
  validate?: (value: string) => string | undefined
  readOnly?: boolean
  currentPage?: number
  mobilePage?: number
}

const CheckboxSubTextField = ({
  name,
  placeholder,
  validate,
  readOnly,
  currentPage,
  mobilePage,
}: CheckboxSubTextFieldProps) => {
  // Skip validation when user hasn't reached this page yet
  const pageAwareValidate = useCallback(
    (value: string) => {
      if (currentPage !== undefined && mobilePage !== undefined && currentPage < mobilePage) {
        return undefined
      }
      return validate?.(value)
    },
    [currentPage, mobilePage, validate],
  )
  const { colors } = useTheme()
  const [isFocused, setIsFocused] = useState(false)
  const getBorderColor = (hasError: boolean) => {
    if (hasError) return colors.inputOutlineError
    if (isFocused) return colors.checkboxOutlineSelected
    return colors.checkboxOutlineInactive
  }
  return (
    <Field name={name} validate={pageAwareValidate}>
      {({ input, meta }) => {
        const hasError = meta.touched && meta.error
        return (
          <InputFieldRFF
            input={input}
            meta={meta}
            placeholder={placeholder}
            testID={`${name}-subTextField`}
            inputContainerStyle={{
              height: 56,
              borderWidth: 1,
              borderColor: getBorderColor(!!hasError),
              borderRadius: 8,
            }}
            handleOnInputFocus={() => setIsFocused(true)}
            handleOnInputBlur={() => setIsFocused(false)}
            readOnly={readOnly}
          />
        )
      }}
    </Field>
  )
}

const DATA_KEYBOARD_MAP = new Map<JSONSchema7TypeName | 'default', KeyboardType>([
  ['string', 'default'],
  ['number', 'number-pad'],
  ['default', 'default'],
])

type CustomInputFieldWidgetProps<P> = { CustomInputFieldComponent?: React.FC<P>; customInputFieldProps?: P }

const TextWidget = <CustomInputFieldProps,>({
  schema: {
    name = '',
    title,
    subTitle,
    subTitleComponent,
    placeholder,
    type,
    backgroundColor,
    maxCharLength,
    multiline,
    numberOfLines,
    widgetProps,
    readOnlyOverride,
    labelBadgeType,
    error,
  },
  formContext: {
    readOnly,
    inputAccessoryViewID,
    returnKeyType,
    handleOnInputFocus,
    setInputRefs,
    handleOnInputBlur,
    getReturnKeyType,
    navigateToNewField,
    areAllInputRefsSet,
  },
  options: { baseInputStyle, inputContainerStyle, labelStyle, labelContainerStyle, isOptionalLabel, autoComplete },
  CustomInputFieldComponent,
  customInputFieldProps,
}: UICoreWidgetProps & CustomInputFieldWidgetProps<CustomInputFieldProps>): ReactElement => {
  const overrideError = widgetProps?.overrideError

  const dataType = isString(type) ? type : 'default'
  const keyboardType = DATA_KEYBOARD_MAP.get(dataType)
  const { colors } = useTheme()
  return (
    <Field
      label={title}
      sublabel={subTitle}
      sublabelComponent={subTitleComponent}
      type='text'
      keyboardType={keyboardType}
      name={name}
      placeholder={placeholder}
      component={InputFieldRFF}
      readOnly={readOnlyOverride || readOnly}
      inputAccessoryViewID={inputAccessoryViewID}
      returnKeyType={returnKeyType}
      backgroundColor={backgroundColor}
      maxCharLength={maxCharLength}
      multiline={multiline}
      numberOfLines={numberOfLines}
      placeholderTextColor={colors.textInactive}
      labelStyle={labelStyle}
      labelContainerStyle={labelContainerStyle}
      isOptionalLabel={isOptionalLabel}
      baseInputStyle={baseInputStyle}
      inputContainerStyle={inputContainerStyle}
      handleOnInputFocus={handleOnInputFocus}
      setInputRefs={setInputRefs}
      handleOnInputBlur={handleOnInputBlur}
      getReturnKeyType={getReturnKeyType}
      navigateToNewField={navigateToNewField}
      areAllInputRefsSet={areAllInputRefsSet}
      CustomInputFieldComponent={CustomInputFieldComponent}
      customInputFieldProps={customInputFieldProps}
      labelBadgeType={labelBadgeType}
      autoComplete={autoComplete}
      {...(error ? { error } : {})}
      {...(overrideError ? { customError: widgetProps?.error } : {})}
      {...widgetProps}
    />
  )
}

const CheckboxWidget = ({
  schema: { name = '', title, buttonType, badge, description, error, subTitle, widgetProps, ...rest },
  options: { baseInputStyle },
  formContext: { readOnly, customCheckboxTitle },
}: UICoreWidgetProps): ReactElement => {
  /**
   * Since we do not use a real input field with type='checkbox', checked
   * does not work with react-final-form and is never triggered. Hence we need to use
   * a type other than type='checkbox', i.e. type='bool' for the field and use value in CheckboxRFF to determine if the field is true/false
   */
  return (
    <Field
      type='bool'
      name={name}
      component={CheckboxRFF}
      title={title ?? customCheckboxTitle}
      label={subTitle}
      readOnly={readOnly}
      buttonType={buttonType}
      badge={badge}
      description={description}
      baseInputStyle={baseInputStyle}
      {...(error ? { error } : {})}
      {...(rest as Dict)}
      {...widgetProps}
    />
  )
}

const TextareaWidget = ({
  schema: {
    name = '',
    title,
    placeholder,
    numberOfLines,
    type,
    maxCharLength,
    showCharLimit,
    labelBadgeType,
    widgetProps,
  },
  formContext: { readOnly, inputAccessoryViewID, animatedSharedValues },
}: UICoreWidgetProps): ReactElement => {
  const dataType = isString(type) ? type : 'default'
  const keyboardType = DATA_KEYBOARD_MAP.get(dataType)
  const { colors } = useTheme()
  return (
    <AnimatedWrapper animatedSharedValues={animatedSharedValues}>
      <Field
        label={title}
        type='text'
        keyboardType={keyboardType}
        name={name}
        placeholder={placeholder}
        component={InputFieldRFF}
        multiline
        numberOfLines={numberOfLines ?? 4}
        maxCharLength={maxCharLength}
        showCharLimit={showCharLimit}
        readOnly={readOnly || widgetProps?.readOnly}
        inputAccessoryViewID={inputAccessoryViewID}
        placeholderTextColor={colors.textInactive}
        labelBadgeType={labelBadgeType}
        {...widgetProps}
      />
    </AnimatedWrapper>
  )
}

const DateWidget = ({
  options: { style, dateFieldDisplay, baseInputStyle, locale: customLocale },
  schema: { name = '', title, placeholder, widgetProps, error },
  formContext: { readOnly, locale, handleOnInputFocus, setInputRefs, handleOnInputBlur },
}: UICoreWidgetProps): ReactElement => {
  /** Error: Rendered fewer hooks than expected. This may be caused by an accidental early return statement.
   * The root cause is likely that @rjsf/core's dynamic widget wrapping (mergeOptions/getWidget) creates an unstable wrapper that conflicts with the compiler's injected useMemoCache hook. Since @rjsf/core predates the React Compiler and does runtime component wrapping, this is a known class of incompatibility.
   * Migration to v5/v6 is not trivial
   */
  'use no memo'
  return (
    <Field
      label={title}
      type='text'
      name={name}
      placeholder={placeholder}
      component={DateFieldRFF}
      readOnly={readOnly}
      locale={customLocale || locale}
      style={style}
      dateFieldDisplay={dateFieldDisplay}
      baseInputStyle={baseInputStyle}
      handleOnInputFocus={handleOnInputFocus}
      handleOnInputBlur={handleOnInputBlur}
      setInputRefs={setInputRefs}
      {...(error ? { error } : {})}
      {...widgetProps}
    />
  )
}

const TimeWidget = ({
  options: { style, baseInputStyle, locale: customLocale },
  schema: { name = '', title, placeholder, widgetProps },
  formContext: { readOnly, locale },
}: UICoreWidgetProps): ReactElement => {
  return (
    <Field
      label={title}
      type='text'
      name={name}
      placeholder={placeholder}
      component={TimeFieldRFF}
      readOnly={readOnly}
      locale={customLocale || locale}
      style={style}
      baseInputStyle={baseInputStyle}
      {...widgetProps}
    />
  )
}

const RangeWidget = ({
  schema: {
    name = '',
    title,
    minimum,
    maximum,
    minLabel,
    midLabel,
    maxLabel,
    default: initValue = 0,
    accessibilityLabelledBy,
    decimalValue, // save the value as a decimal value
    show,
    hidden,
    stepLabels,
  },
  formContext: { readOnly, animatedSharedValues, externalValues },
  options: { percentage, showDivider, rangeContainerStyle, rangeLabelStyle, style },
}: UICoreWidgetProps): ReactElement => {
  let parse = (value: any) => value
  let format = (value: any) => value
  const decimalScale = 10
  if (decimalValue) {
    parse = (value: any) => {
      if (value && isNumber(value)) {
        return value / decimalScale
      }
      return value
    }
    format = (value: any) => {
      if (value && isNumber(value)) {
        return Math.floor(value * decimalScale)
      }
      return value
    }
  }
  return (
    <AnimatedWrapper animatedSharedValues={animatedSharedValues}>
      <Field
        label={title}
        hidden={hidden || show === false}
        type='text'
        name={name}
        parse={parse}
        format={format}
        initValue={initValue}
        component={SliderFieldRFF}
        accessibilityLabelledBy={accessibilityLabelledBy}
        minimum={minimum}
        maximum={maximum}
        minLabel={minLabel}
        midLabel={midLabel}
        maxLabel={maxLabel}
        readOnly={readOnly}
        percentage={percentage}
        stepLabels={stepLabels}
        showDivider={showDivider}
        rangeContainerStyle={rangeContainerStyle}
        rangeLabelStyle={rangeLabelStyle}
        customStyles={style}
        externalValuesStyleOverride={externalValues?.stylesOverride}
      />
    </AnimatedWrapper>
  )
}

const MulticolorSliderWidget = ({
  schema: { name = '', title, minimum, maximum, minLabel, maxLabel, default: initValue = 0, accessibilityLabelledBy },
  options: { rangeLabelStyle, rangeContainerStyle, disableThumbAnimationOnDrag },
}: UICoreWidgetProps): ReactElement => {
  return (
    <Field
      label={title}
      type='text'
      name={name}
      initValue={initValue}
      component={MulticolorSliderFieldRFF}
      accessibilityLabelledBy={accessibilityLabelledBy}
      minimum={minimum}
      maximum={maximum}
      minLabel={minLabel}
      maxLabel={maxLabel}
      rangeLabelStyle={rangeLabelStyle}
      rangeContainerStyle={rangeContainerStyle}
      disableThumbAnimationOnDrag={disableThumbAnimationOnDrag}
    />
  )
}

const RadioWidget = ({
  options,
  schema: {
    name = '',
    title = '',
    titleSecondary,
    subTitle,
    subTitleComponent,
    titleAlignment,
    largeSubTitle,
    correctValue,
    correctValueDetails,
    type,
    buttonType,
    accessibilityLabelledBy,
    titleInfoPopoverConfig,
    titleInfoPopoverContent,
    minLabel,
    maxLabel,
  },
  formContext: { readOnly, showAnswers, formDisabled, animatedSharedValues, ...contextProps },
  options: { baseInputStyle, customTextStyle, labelContainerStyle, buttonStyle },
}: UICoreWidgetProps): ReactElement => {
  let buttons
  let isYesNo = false
  const shouldHighlight = showAnswers && !isNil(correctValue)
  if (type === 'boolean') {
    // FIXME: This is not internationalized, only works for English
    const yesText = get(options, 'enumOptions[0].label', '') as string
    const noText = get(options, 'enumOptions[1].label', '') as string
    if (capitalize(yesText) === 'Yes' && capitalize(noText) === 'No') isYesNo = true
    buttons = [
      {
        // By default the label for the first option is 'yes'
        label: capitalize(yesText),
        value: 1,
        highlight: shouldHighlight ? correctValue === 1 : undefined,
      },
      {
        // By default the label for the second option is 'no'
        label: capitalize(noText),
        value: 0,
        highlight: shouldHighlight ? correctValue === 0 : undefined,
      },
    ]
  } else {
    buttons =
      isArray(options.enumOptions) &&
      options.enumOptions.map((option) => {
        option.highlight = shouldHighlight ? correctValue === option.value : undefined
        if (option.schema) {
          option.description = option.schema.description
          option.badge = option.schema.badge
          option.disableBadgeDisplay = option.schema.disableBadgeDisplay
          option.inactive = option.schema.inactive
          option.imageComponent = Illustrations[option.schema.imageComponent]
        }
        return option
      })
  }
  return (
    <AnimatedWrapper animatedSharedValues={animatedSharedValues}>
      <Field
        label={title}
        labelInfoPopover={
          titleInfoPopoverContent ? { content: titleInfoPopoverContent, ...titleInfoPopoverConfig } : undefined
        }
        subLabel={subTitle}
        subLabelComponent={subTitleComponent}
        labelSecondary={titleSecondary}
        labelAlignment={titleAlignment}
        largeSubLabel={largeSubTitle}
        type='text'
        name={name}
        options={options}
        component={RadioGroupRFF}
        buttonType={buttonType}
        readOnly={readOnly || options.readonly}
        buttons={buttons}
        showDescription={showAnswers && correctValueDetails}
        correctValueDetails={correctValueDetails}
        correctValue={correctValue}
        isYesNo={isYesNo}
        accessibilityLabelledBy={accessibilityLabelledBy}
        baseInputStyle={baseInputStyle}
        buttonStyle={buttonStyle}
        contextProps={contextProps}
        disabled={formDisabled || options.disabled}
        minLabel={minLabel}
        maxLabel={maxLabel}
        customTextStyle={customTextStyle}
        labelContainerStyle={labelContainerStyle}
      />
    </AnimatedWrapper>
  )
}

type RadioSubDateFieldConfig = {
  name: string
  title?: string
  minDate?: Date | null
  maxDate?: Date | null
}

type RadioSubDateFieldProps = {
  config: RadioSubDateFieldConfig
  isRequired?: boolean
  readOnly?: boolean
  locale?: string
  intl?: IntlShape
  previousFieldName?: string
  currentPage?: number
  mobilePage?: number
}

const RadioSubDateFieldContainer = styled(View)(({ theme }) => ({
  marginLeft: theme.spacing['44px'],
}))

const RadioSubDateField = ({
  config,
  isRequired,
  readOnly,
  locale,
  intl,
  previousFieldName,
  currentPage,
  mobilePage,
}: RadioSubDateFieldProps) => {
  const form = useForm()
  // Subscribe to each field directly for reliable re-renders
  const currentField = useField(config.name, { subscription: { value: true } })
  const previousField = useField(previousFieldName || config.name, { subscription: { value: true } })

  const placeholder = isRequired && config.title ? `${config.title} *` : config.title
  const previousFieldValue: string | undefined = previousFieldName ? previousField?.input.value : undefined
  const currentFieldValue: string | undefined = currentField.input.value

  // Use previous field's date as minimum date for this field
  const minDateFromPreviousField = parseDateString(previousFieldValue, locale || 'en-US')
  const minDate = minDateFromPreviousField ?? config.minDate

  // Auto-update end date to match start date if end date < start date
  useEffect(() => {
    if (previousFieldValue && currentFieldValue) {
      const parseLocale = locale || 'en-US'
      const startDate = parseDateString(previousFieldValue, parseLocale)
      const endDate = parseDateString(currentFieldValue, parseLocale)

      if (startDate && endDate && endDate < startDate) {
        form.change(config.name, previousFieldValue)
      }
    }
  }, [previousFieldValue, currentFieldValue, form, config.name, locale])

  const validate = (value: string) => {
    // Skip validation when user hasn't reached this page yet
    if (currentPage !== undefined && mobilePage !== undefined && currentPage < mobilePage) {
      return undefined
    }
    if (isRequired && !value?.trim()) {
      return intl?.formatMessage(thisFieldIsRequiredErrorMessage)
    }
    return undefined
  }

  const dateFieldStyle = { marginBottom: 8 }

  return (
    <Field
      name={config.name}
      placeholder={placeholder}
      component={DateFieldRFF}
      validate={validate}
      readOnly={readOnly}
      locale={locale}
      inputStyle={dateFieldStyle}
      baseInputStyle={dateFieldStyle}
      appendFormat={false}
      borderRadius={8}
      customHeight={56}
      styledReadOnly
      minDate={minDate}
      maxDate={config.maxDate}
      syncExternalChanges
    />
  )
}

type EnumSubDateFieldsConfig = Record<string, RadioSubDateFieldConfig[]>

const RadioWidgetV2 = ({
  options,
  schema: {
    name = '',
    title = '',
    titleSecondary,
    subTitle,
    subTitleComponent,
    titleAlignment,
    largeSubTitle,
    correctValue,
    correctValueDetails,
    type,
    accessibilityLabelledBy,
    titleInfoPopoverConfig,
    titleInfoPopoverContent,
    minLabel,
    maxLabel,
    validation,
    mobilePage,
  },
  formContext: {
    readOnly,
    showAnswers,
    formDisabled,
    animatedSharedValues,
    locale,
    intl,
    currentPage,
    ...contextProps
  },
  options: { baseInputStyle, customTextStyle, labelContainerStyle, enumSubDateFields },
}: UICoreWidgetProps & {
  options: { enumSubDateFields?: EnumSubDateFieldsConfig }
}): ReactElement => {
  const { input } = useField(name)
  const selectedValue = input.value

  let buttons
  let isYesNo = false
  const shouldHighlight = showAnswers && !isNil(correctValue)
  const isRequired = validation?.isRequired

  if (type === 'boolean') {
    const yesText = get(options, 'enumOptions[0].label', '') as string
    const noText = get(options, 'enumOptions[1].label', '') as string
    if (capitalize(yesText) === 'Yes' && capitalize(noText) === 'No') isYesNo = true
    buttons = [
      {
        label: capitalize(yesText),
        value: 1,
        highlight: shouldHighlight ? correctValue === 1 : undefined,
      },
      {
        label: capitalize(noText),
        value: 0,
        highlight: shouldHighlight ? correctValue === 0 : undefined,
      },
    ]
  } else {
    buttons =
      isArray(options.enumOptions) &&
      options.enumOptions.map((option) => ({
        ...option,
        highlight: shouldHighlight ? correctValue === option.value : undefined,
        description: option.schema?.description,
        badge: option.schema?.badge,
        disableBadgeDisplay: option.schema?.disableBadgeDisplay,
        inactive: option.schema?.inactive,
        imageComponent: option.schema?.imageComponent ? Illustrations[option.schema.imageComponent] : undefined,
      }))
  }

  const selectedEnumKey = typeof selectedValue === 'string' ? selectedValue : String(selectedValue)
  const subDateFields = enumSubDateFields?.[selectedEnumKey]
  const radioBaseInputStyle = subDateFields
    ? { ...(typeof baseInputStyle === 'object' && baseInputStyle !== null ? baseInputStyle : {}), marginBottom: 0 }
    : baseInputStyle

  return (
    <AnimatedWrapper animatedSharedValues={animatedSharedValues}>
      <Field
        label={title}
        labelInfoPopover={
          titleInfoPopoverContent ? { content: titleInfoPopoverContent, ...titleInfoPopoverConfig } : undefined
        }
        subLabel={subTitle}
        subLabelComponent={subTitleComponent}
        labelSecondary={titleSecondary}
        labelAlignment={titleAlignment}
        largeSubLabel={largeSubTitle}
        type='text'
        name={name}
        options={options}
        component={RadioGroupRFF}
        buttonType={RadioButtonType.CIRCLE_TEXT_OUTLINE}
        readOnly={readOnly || options.readonly}
        buttons={buttons}
        showDescription={showAnswers && correctValueDetails}
        correctValueDetails={correctValueDetails}
        correctValue={correctValue}
        isYesNo={isYesNo}
        accessibilityLabelledBy={accessibilityLabelledBy}
        baseInputStyle={radioBaseInputStyle}
        contextProps={contextProps}
        disabled={formDisabled || options.disabled}
        minLabel={minLabel}
        maxLabel={maxLabel}
        customTextStyle={customTextStyle}
        labelContainerStyle={labelContainerStyle}
      />
      {subDateFields &&
        subDateFields.map((dateFieldConfig, index) => (
          <RadioSubDateFieldContainer
            key={dateFieldConfig.name}
            testID={`${name}-subDateField-${dateFieldConfig.name}`}
          >
            <RadioSubDateField
              config={dateFieldConfig}
              isRequired={!!isRequired}
              readOnly={!!readOnly}
              locale={locale as string | undefined}
              intl={intl}
              previousFieldName={index > 0 ? subDateFields[index - 1].name : undefined}
              currentPage={currentPage as number | undefined}
              mobilePage={mobilePage}
            />
          </RadioSubDateFieldContainer>
        ))}
    </AnimatedWrapper>
  )
}

const ImageRadioWidget = ({
  options,
  schema: {
    name = '',
    title,
    subTitle,
    subTitleComponent,
    titleAlignment,
    correctValue,
    correctValueDetails,
    largeSubTitle,
  },
  formContext: { readOnly, showAnswers, formDisabled, animatedSharedValues, ...contextProps },
}: UICoreWidgetProps): ReactElement => {
  const buttons =
    isArray(options.enumOptions) &&
    options.enumOptions.map((option) => {
      if (option.schema) {
        option.inactive = option.schema.inactive
        option.imageComponent = Illustrations[option.schema.imageComponent]
        option.details = option.schema.details
      }
      return option
    })
  return (
    <AnimatedWrapper animatedSharedValues={animatedSharedValues}>
      <Field
        label={title}
        subLabel={subTitle}
        subLabelComponent={subTitleComponent}
        largeSubLabel={largeSubTitle}
        contextProps={contextProps}
        labelAlignment={titleAlignment}
        type='text'
        name={name}
        options={options}
        component={ImageRadioGroupRFF}
        readOnly={readOnly}
        buttons={buttons}
        showDescription={showAnswers && correctValueDetails}
        correctValueDetails={correctValueDetails}
        correctValue={correctValue}
        isYesNo={false}
        disabled={formDisabled}
      />
    </AnimatedWrapper>
  )
}

const MaskedInputWidget = ({
  options: { mask, style, baseInputStyle },
  schema: { name = '', title, placeholder, parseData, formatData },
  formContext: { readOnly, inputAccessoryViewID },
}: UICoreWidgetProps): ReactElement => {
  return (
    <Field
      style={style}
      mask={mask}
      label={title}
      type='text'
      name={name}
      placeholder={placeholder}
      component={MaskedInputRFF}
      readOnly={readOnly}
      inputAccessoryViewID={inputAccessoryViewID}
      parse={
        !isEmpty(parseData) && parseData !== undefined
          ? (value) => {
              if (!isEmpty(value) && value !== undefined) {
                const functionToRun = parseData[0]
                const params = parseData.slice(1)
                if (!isEmpty(parseFunctions) && isFunction(parseFunctions[functionToRun])) {
                  value = parseFunctions[functionToRun](value, params)
                }
              }
              return value
            }
          : undefined
      }
      format={
        !isEmpty(formatData) && formatData !== undefined
          ? (value) => {
              if (!isEmpty(value) && value !== undefined) {
                const functionToRun = formatData[0]
                const params = formatData.slice(1)
                if (!isEmpty(formatFunctions) && isFunction(formatFunctions[functionToRun])) {
                  value = formatFunctions[functionToRun](value, params)
                }
              }
              return value
            }
          : undefined
      }
      baseInputStyle={baseInputStyle}
    />
  )
}

const SelectWidget = ({
  options: { enumOptions, selectFieldStyle, useStringValue, baseInputStyle, customTextStyle },
  schema: { name = '', title, placeholder, widgetProps },
  formContext: { readOnly, useTriageStyling, animatedSharedValues },
}: UICoreWidgetProps): ReactElement => {
  return (
    <AnimatedWrapper animatedSharedValues={animatedSharedValues}>
      <Field
        label={title}
        type='text'
        name={name}
        options={enumOptions}
        component={SelectFieldRFF}
        placeholder={placeholder}
        readOnly={readOnly}
        customWebStyle={selectFieldStyle}
        customIOSStyle={selectFieldStyle}
        customAndroidStyle={selectFieldStyle}
        useStringValue={useStringValue}
        baseInputStyle={baseInputStyle}
        useTriageStyling={useTriageStyling}
        customTextStyle={customTextStyle}
        {...widgetProps}
      />
    </AnimatedWrapper>
  )
}

const CheckboxGroupWidget = ({
  schema: {
    name = '',
    title,
    subTitle,
    titleAlignment,
    largeLabel,
    enum: enumOptions,
    enumNames,
    enumDescriptions,
    enumExclusiveValuesGroups,
    buttonType,
    badge,
    titleSecondary,
    noneOfTheAboveCheckboxId,
    selectAllCheckboxId,
    validation,
    widgetProps,
    mobilePage,
    ModalContentConfig: modalContentConfig,
    subTitleLinkStyle,
  },
  formContext: { readOnly, showAsteriskForRequiredFields, intl, schema: formSchemaFromContext, currentPage },
  options: { baseInputStyle, labelStyle, labelContainerStyle, inputContainerStyle, customTextStyle, enumSubTextFields },
}: UICoreWidgetProps & {
  options: { enumSubTextFields?: Record<string, { title?: string; name?: string }> }
}): ReactElement => {
  const supportsSubTextFields = buttonType === CheckboxButtonType.CHECKBOX_DESCRIPTION_OUTLINE
  const isRequired = validation?.isRequired
  const formSchema = formSchemaFromContext as FieldSchema | undefined

  const getSubTextFieldValidate = useCallback(
    (subTextFieldName: string) => {
      const subFieldSchema = formSchema?.properties?.[subTextFieldName]
      const charLimit = subFieldSchema?.validation?.characterLimit

      return (value: string): string | undefined => {
        if (isRequired && !value?.trim()) {
          return intl?.formatMessage(thisFieldIsRequiredErrorMessage) as string
        }
        if (typeof charLimit === 'number' && intl) {
          const errors = characterLimit(value, subTextFieldName, charLimit, intl.formatMessage)
          return errors[subTextFieldName] as string | undefined
        }
        return undefined
      }
    },
    [formSchema, isRequired, intl],
  )

  const options = useMemo(
    () =>
      enumOptions?.map((enumValue, index) => {
        const enumKey = typeof enumValue === 'string' ? enumValue : String(enumValue)
        const subTextField = supportsSubTextFields ? enumSubTextFields?.[enumKey] : undefined
        return {
          id: enumValue,
          text: enumNames?.[index] ?? enumValue,
          description: enumDescriptions?.[index],
          group: enumExclusiveValuesGroups?.[index],
          ...(subTextField && {
            SubTextField: () => (
              <CheckboxSubTextField
                name={subTextField.name ?? ''}
                placeholder={
                  showAsteriskForRequiredFields && isRequired && subTextField.title
                    ? `${subTextField.title} *`
                    : subTextField.title
                }
                validate={getSubTextFieldValidate(subTextField.name ?? '')}
                readOnly={readOnly as boolean}
                currentPage={currentPage as number | undefined}
                mobilePage={mobilePage}
              />
            ),
          }),
        }
      }),
    [
      enumOptions,
      enumNames,
      enumDescriptions,
      enumExclusiveValuesGroups,
      supportsSubTextFields,
      enumSubTextFields,
      showAsteriskForRequiredFields,
      isRequired,
      readOnly,
      getSubTextFieldValidate,
      currentPage,
      mobilePage,
    ],
  )
  return (
    <Field
      readOnly={readOnly || widgetProps?.readOnly}
      name={name}
      component={CheckboxGroupRFF}
      label={title}
      labelSecondary={titleSecondary}
      subLabel={subTitle}
      largeLabel={largeLabel}
      labelAlignment={titleAlignment}
      options={options}
      buttonType={buttonType}
      badge={badge}
      baseInputStyle={baseInputStyle}
      labelStyle={labelStyle}
      labelContainerStyle={labelContainerStyle}
      inputContainerStyle={inputContainerStyle}
      customTextStyle={customTextStyle}
      noneOfTheAboveCheckboxId={noneOfTheAboveCheckboxId}
      selectAllCheckboxId={selectAllCheckboxId}
      subLabelModalContentConfig={modalContentConfig}
      subLabelLinkStyle={subTitleLinkStyle}
    />
  )
}

const ImageCheckboxWidget = ({
  schema: {
    name = '',
    title,
    subTitle,
    titleAlignment,
    enum: enumOptions,
    enumNames,
    enumExclusiveValuesGroups,
    enumImageComponents,
    buttonType,
    badge,
    titleSecondary,
    noneOfTheAboveCheckboxId,
    limitSelected,
    largeSubTitle,
  },
  formContext: { readOnly, animatedSharedValues },
  options: { baseInputStyle, labelStyle, labelContainerStyle, inputContainerStyle, customTextStyle },
}: UICoreWidgetProps): ReactElement => {
  const options = enumOptions?.map((enumValue, index) => ({
    id: enumValue,
    text: enumNames?.[index] ?? enumValue,
    group: enumExclusiveValuesGroups?.[index],
    ImageComponent: enumImageComponents?.[index]
      ? Illustrations[enumImageComponents[index] as keyof typeof Illustrations]
      : undefined,
  }))
  return (
    <AnimatedWrapper animatedSharedValues={animatedSharedValues}>
      <Field
        type='text'
        readOnly={readOnly}
        name={name}
        component={ImageCheckboxGroupRFF}
        label={title}
        labelSecondary={titleSecondary}
        subLabel={subTitle}
        largeSubLabel={largeSubTitle}
        labelAlignment={titleAlignment}
        options={options}
        buttonType={buttonType}
        badge={badge}
        baseInputStyle={baseInputStyle}
        labelStyle={labelStyle}
        labelContainerStyle={labelContainerStyle}
        inputContainerStyle={inputContainerStyle}
        customTextStyle={customTextStyle}
        noneOfTheAboveCheckboxId={noneOfTheAboveCheckboxId}
        limitSelected={limitSelected}
      />
    </AnimatedWrapper>
  )
}

const TypeAheadWidget = ({
  schema: {
    name = '',
    title,
    subTitle,
    options,
    placeholder,
    multiSelect,
    allowUserInput,
    dataSource,
    saveFieldAsArray,
    widgetProps,
  },
  options: { baseInputStyle, inputContainerStyle, dropdownContainerStyle },
  formContext: {
    readOnly,
    useMultiSelectTypeAhead,
    saveSingleSelectFieldsAsArray,
    postExternalMatches,
    setInputRefs,
    initialTypeAheadValues,
    animatedSharedValues,
    ...contextProps
  },
}: UICoreWidgetProps): ReactElement => {
  let fromContext: string | undefined
  if (typeof options === 'object' && 'fromContext' in options) {
    fromContext = options.fromContext
    options = (options.fromContext && contextProps[options.fromContext]) || []
  }
  const optionsWithIds =
    !useMultiSelectTypeAhead && Array.isArray(options)
      ? options.map((option: string) => ({ text: option, id: option }))
      : options

  return (
    <AnimatedWrapper animatedSharedValues={animatedSharedValues}>
      {useMultiSelectTypeAhead ? (
        <Field
          name={name}
          component={TypeAheadRFF}
          label={title}
          subLabel={subTitle}
          options={options}
          customOptionsConfig={
            Array.isArray(options) && isObject(options[0])
              ? new TypeAheadCustomOptionsConfig(
                  options,
                  undefined,
                  undefined,
                  undefined,
                  undefined,
                  ValueFetchMode.LEGACY_LABEL,
                )
              : undefined
          }
          readOnly={readOnly || widgetProps?.readOnly}
          multiSelect={multiSelect}
          saveSingleSelectFieldsAsArray={saveFieldAsArray ?? saveSingleSelectFieldsAsArray}
          allowUserInput={allowUserInput}
          placeholder={placeholder}
          showNoResultsDialog={false}
          dataSource={dataSource}
          postExternalMatches={postExternalMatches}
          fromContext={fromContext}
          baseInputStyle={baseInputStyle}
          inputContainerStyle={inputContainerStyle}
          dropdownContainerStyle={dropdownContainerStyle}
          setInputRefs={setInputRefs}
          initialValues={initialTypeAheadValues}
        />
      ) : (
        /**
         * Until we implement multiselect for mobile typeAhead
         * we can map to our checkbox component instead.
         */
        <Field
          readOnly={readOnly || widgetProps?.readOnly}
          name={name}
          component={CheckboxGroupRFF}
          label={title}
          options={optionsWithIds}
        />
      )}
    </AnimatedWrapper>
  )
}

const MultiSelectWidget = ({
  schema: { name = '', title, subTitle, enumNames, enum: enumOptions },
  formContext: { readOnly },
}: UICoreWidgetProps): ReactElement => {
  const optionsWithIds = Array.isArray(enumNames)
    ? enumNames.map((enumName: string, index: number) => ({
        text: enumName,
        id: enumOptions ? enumOptions[index] : index,
      }))
    : []

  return (
    <Field
      name={name}
      component={MultiSelectRFF}
      label={title}
      subLabel={subTitle}
      options={optionsWithIds}
      readOnly={readOnly}
    />
  )
}

const InternationalPhoneInputWidget = ({
  schema: { name = '', title },
  formContext: { customerCountryList, userCountryIsoCode, readOnly },
  options: {
    baseInputStyle,
    toolTipContent,
    toolTipTriggerConfig,
    toolTipPlacement,
    tooltipSize,
    hideInputFlag = false,
    linkUserCountryIsoCode = false,
    enableAutoComplete = false,
  },
}: UICoreWidgetProps): ReactElement => {
  const { formatMessage } = useIntl()
  const callingCodeRef = useRef<string | undefined>(undefined)

  const handleCallingCodeChange = useCallback((callingCode: string | undefined) => {
    callingCodeRef.current = callingCode
  }, [])

  const validateCallingCode = useCallback(() => {
    if (!callingCodeRef.current) {
      return formatMessage(unsupportedCountryCodeErrorMessage)
    }
    return undefined
  }, [formatMessage])

  return (
    <Field
      name={name}
      label={title}
      type='text'
      component={InternationalPhoneNumberEntryRFF}
      validate={validateCallingCode}
      userCountryIsoCode={userCountryIsoCode}
      customerCountryList={customerCountryList}
      readOnly={readOnly}
      baseInputStyle={baseInputStyle}
      toolTipContent={toolTipContent}
      toolTipTriggerConfig={toolTipTriggerConfig}
      toolTipPlacement={toolTipPlacement}
      toolTipSize={tooltipSize}
      hideInputFlag={hideInputFlag}
      linkUserCountryIsoCode={linkUserCountryIsoCode}
      enableAutoComplete={enableAutoComplete}
      onCallingCodeChange={handleCallingCodeChange}
    />
  )
}

const WordCloudWidget = ({
  schema: {
    name = '',
    title,
    subTitle = '',
    titleAlignment,
    options,
    enum: enumOptions,
    enumNames,
    largeSubTitle,
    modifier,
  },
  formContext,
  options: { customStyles },
}: UICoreWidgetProps): ReactElement => {
  let optionsList = []
  let fromContext: string | undefined
  if (typeof options === 'object' && 'fromContext' in options) {
    fromContext = options.fromContext
    optionsList = (formContext.wordCloudWords?.[options.fromContext as string] as Array<string>) || []
  } else {
    optionsList = enumOptions?.map((enumValue, index) => enumNames?.[index] ?? enumValue) ?? []
  }

  return (
    <AnimatedWrapper animatedSharedValues={formContext.animatedSharedValues}>
      <Field
        name={name}
        component={WordCloudRFF}
        label={title}
        subLabel={subTitle}
        labelAlignment={titleAlignment}
        largeSubLabel={largeSubTitle}
        options={optionsList}
        fromContext={fromContext}
        showCategories={formContext.showWordCloudCategories}
        modifier={modifier}
        disabled={formContext.formDisabled}
        customStyles={customStyles}
      />
    </AnimatedWrapper>
  )
}

interface InlineBannerWidgetProps extends UICoreWidgetProps {
  options: {
    bannerMessage: React.ReactNode | string
    title?: string
    textSize?: BodyTextSize
    marginTop?: string
    marginBottom?: string
  } & { [key: string]: any } // This to bypass rjsf type checking for options, ReactNode is a valid type
}

const InlineErrorBannerWidget = ({
  options: { bannerMessage, marginBottom, marginTop },
  schema: { name },
  formContext: { inlineErrorBannerMessage },
}: InlineBannerWidgetProps): ReactElement | null => {
  return inlineErrorBannerMessage || bannerMessage ? (
    <InlineErrorBanner
      testId={name}
      text={bannerMessage ?? inlineErrorBannerMessage}
      marginBottom={marginBottom}
      marginTop={marginTop}
    />
  ) : null
}

const InlineWarningBannerWidget = ({
  options: { bannerMessage, title, textSize, marginTop },
  schema: { name },
  formContext: { inlineWarningBannerMessage, inlineWarningBannerTitle },
}: InlineBannerWidgetProps): ReactElement | null => {
  return inlineWarningBannerMessage || bannerMessage ? (
    <InlineWarningBanner
      testId={name}
      text={bannerMessage ?? inlineWarningBannerMessage}
      title={inlineWarningBannerTitle ?? title}
      textSize={textSize}
      marginTop={marginTop}
    />
  ) : null
}

const InlineSuccessBannerWidget = ({
  schema: { name },
  formContext: { inlineSuccessBannerMessage },
}: UICoreWidgetProps): ReactElement | null => {
  return inlineSuccessBannerMessage ? <InlineSuccessBanner testId={name} text={inlineSuccessBannerMessage} /> : null
}

const InlineInfoBannerWidget = ({
  options: { marginTop, marginBottom },
  schema: { name },
  formContext: { inlineInfoBannerMessage },
}: InlineBannerWidgetProps): ReactElement | null => {
  return inlineInfoBannerMessage ? (
    <InlineInfoBanner testId={name} text={inlineInfoBannerMessage} marginTop={marginTop} marginBottom={marginBottom} />
  ) : null
}

const CountrySelectorWidget = ({
  schema: { name = '', title, error },
  formContext: { customerCountryList, userCountryIsoCode, userCountryName },
  options: { baseInputStyle, inputContainerStyle, dropdownContainerStyle },
}: UICoreWidgetProps): ReactElement => {
  return (
    <Field
      name={name}
      label={title}
      error={error}
      component={CountrySelectorRFF}
      userCountryIsoCode={userCountryIsoCode}
      userCountryName={userCountryName}
      customerCountryList={customerCountryList}
      baseInputStyle={baseInputStyle}
      inputContainerStyle={inputContainerStyle}
      dropdownContainerStyle={dropdownContainerStyle}
    />
  )
}

const PasswordWidget = ({
  schema: {
    name = '',
    title,
    error,
    widgetProps,
    hidePasswordChecklistOnBlur,
    showPasswordChecklistByDefault,
    passwordAutoComplete,
    elevateChecklist,
  },
  formContext: {
    returnKeyType,
    passwordPolicy,
    inputAccessoryViewID,
    handleOnInputFocus,
    setInputRefs,
    handleOnInputBlur,
    getReturnKeyType,
    navigateToNewField,
    areAllInputRefsSet,
  },
  options: { baseInputStyle, inputContainerStyle, labelContainerStyle },
}: UICoreWidgetProps): ReactElement => {
  return (
    <Field
      name={name}
      label={title}
      error={error}
      component={PasswordFieldRFF}
      passwordPolicy={passwordPolicy}
      validateOnTouch={true}
      {...widgetProps}
      baseInputStyle={baseInputStyle}
      inputContainerStyle={inputContainerStyle}
      labelContainerStyle={labelContainerStyle}
      showPasswordChecklistByDefault={showPasswordChecklistByDefault}
      hidePasswordChecklistOnBlur={hidePasswordChecklistOnBlur}
      elevateChecklist={elevateChecklist}
      autoComplete={passwordAutoComplete}
      inputAccessoryViewID={inputAccessoryViewID}
      handleOnInputFocus={handleOnInputFocus}
      setInputRefs={setInputRefs}
      handleOnInputBlur={handleOnInputBlur}
      returnKeyType={returnKeyType}
      getReturnKeyType={getReturnKeyType}
      navigateToNewField={navigateToNewField}
      areAllInputRefsSet={areAllInputRefsSet}
    />
  )
}

const RequiredCheckboxWidget = ({
  schema: { name = '' },
  options: { headerText, checkboxLabel, shouldShowBanner, bannerMessage, baseInputStyle },
}: RequiredCheckboxWithBannerWidgetProps): ReactElement => {
  return (
    <RequiredCheckboxWithBanner
      headerText={headerText}
      checkboxLabel={checkboxLabel}
      checkboxName={name}
      bannerText={bannerMessage}
      shouldShowBanner={!!shouldShowBanner}
      baseInputStyle={baseInputStyle}
    />
  )
}

const DatePickerSelectWidget = ({
  options: { minYear, maxYear, numYears, yearOrder, baseInputStyle, selectFieldStyle, labelContainerStyle },
  schema: { name = '', title, widgetProps },
}: UICoreWidgetProps): ReactElement => {
  return (
    <Field
      label={title}
      type='text'
      name={name}
      component={DatePickerSelectFieldRFF}
      minYear={minYear}
      maxYear={maxYear}
      numYears={numYears}
      yearOrder={yearOrder}
      baseInputStyle={baseInputStyle}
      selectFieldStyle={selectFieldStyle}
      labelContainerStyle={labelContainerStyle}
      {...widgetProps}
    />
  )
}

const HealthPlanSelectorWidget = ({
  schema: { name = '', title, error },
  formContext: { healthPlan, healthPlansList, nonIntegratedHealthPlansList },
  options: { baseInputStyle, inputContainerStyle, dropdownContainerStyle },
}: UICoreWidgetProps): ReactElement => {
  return (
    <Field
      name={name}
      label={title}
      error={error}
      component={HealthPlanSelectorRFF}
      healthPlan={healthPlan}
      healthPlansList={healthPlansList}
      nonIntegratedHealthPlansList={nonIntegratedHealthPlansList}
      baseInputStyle={baseInputStyle}
      inputContainerStyle={inputContainerStyle}
      dropdownContainerStyle={dropdownContainerStyle}
    />
  )
}

const ButtonRangeWidget = ({
  schema: {
    accessibilityLabelledBy,
    default: initValue = 0,
    hidden,
    name = '',
    minimum,
    maximum,
    minLabel,
    maxLabel,
    show,
    title,
  },
  options: { rangeContainerStyle },
}: UICoreWidgetProps): ReactElement => {
  return (
    <Field
      accessibilityLabelledBy={accessibilityLabelledBy}
      component={ButtonRangeGroupRFF}
      hidden={hidden || show === false}
      initValue={initValue}
      label={title}
      maximum={maximum}
      maxLabel={maxLabel}
      minimum={minimum}
      minLabel={minLabel}
      name={name}
      rangeContainerStyle={rangeContainerStyle}
    />
  )
}

const AddIconWidget = ({
  schema: { name },
  options: { onPress, isOpen, text, size, style },
}: AddIconWidgetProps): ReactElement => {
  const { colors } = useTheme()

  return (
    <Pressable onPress={onPress} disabled={isOpen} accessible={!isOpen} style={style} testID={name}>
      {!isOpen && <AddIcon width={size} fillColor={colors.iconActive} />}
      {text && (
        <BodyText
          size={BodyTextSize.DEFAULT}
          style={{ paddingLeft: isOpen ? 0 : 8 }}
          text={text}
          color={isOpen ? colors.textPrimary : colors.textActive}
        />
      )}
    </Pressable>
  )
}

const EditableTextWidget = (props: UICoreWidgetProps): ReactElement => {
  const customInputFieldProps: Omit<EditableInputFieldProps, keyof InputFieldProps> = {
    editButtonText: props.options.editButtonText,
    editingLabel: props.options.editingLabel as string,
    clearValueOnEdit: props.options.clearValueOnEdit as boolean,
    showOriginalValueOnEdit: props.options.showOriginalValueOnEdit as boolean,
    onPressEdit: props.options.onPressEdit as () => void | undefined,
  }
  return (
    <TextWidget
      CustomInputFieldComponent={EditableInputField}
      customInputFieldProps={customInputFieldProps}
      {...props}
    />
  )
}

const FileWidget = ({
  schema: {
    name = '',
    title,
    buttonType: _buttonType,
    badge: _badge,
    description: _description,
    error,
    widgetProps,
    ...rest
  },
  options: { baseInputStyle, onUpload, onUploadSuccessful, acceptedFileTypes, maxFileSize, onError, validateFile },
  formContext: { readOnly },
}: UICoreWidgetProps): ReactElement => {
  return (
    <Field
      type='file'
      name={name}
      component={FileUploadRFF}
      label={title}
      placeholder={widgetProps?.placeholder}
      acceptedFileTypes={acceptedFileTypes}
      maxFileSize={maxFileSize}
      disabled={readOnly}
      baseInputStyle={baseInputStyle}
      onUpload={onUpload}
      onUploadSuccessful={onUploadSuccessful}
      onError={onError}
      validateFile={validateFile}
      {...(error ? { error } : {})}
      {...(rest as Dict)}
      {...widgetProps}
    />
  )
}

const GenAiHeaderWidget = ({ formContext }: UICoreWidgetProps): ReactElement => {
  return (
    <AnimatedWrapper
      animatedSharedValues={formContext?.externalValues?.genAiAnimatedSharedValues as AnimatedSharedValues | undefined}
    >
      <GenAiHeader
        text={formContext?.externalValues?.genAiText as string | undefined}
        shouldAnimate={formContext?.externalValues?.shouldAnimateGenAIText as boolean | undefined}
        estimatedLines={
          formContext?.externalValues?.genAiHeaderLines as { desktop: number; mobile: number } | undefined
        }
      />
    </AnimatedWrapper>
  )
}

const UserContactInformationCardWidget = ({ formContext }: UICoreWidgetProps): ReactElement => {
  const externalValues = formContext?.externalValues
  return (
    <UserContactInformationCard
      userName={externalValues?.userContactInfoUserName as string}
      dateOfBirth={externalValues?.userContactInfoDateOfBirth as string}
      emailAddress={externalValues?.userContactInfoEmailAddress as string}
      careNavigatorEmail={externalValues?.userContactInfoCareNavigatorEmail as string | undefined}
      careNavigatorPhone={externalValues?.userContactInfoCareNavigatorPhone as string | undefined}
      phoneExtension={externalValues?.userContactInfoPhoneExtension as string | undefined}
      hasDependents={externalValues?.userContactInfoHasDependents as boolean | undefined}
      userIsDependent={externalValues?.userContactInfoUserIsDependent as boolean | undefined}
      dependentInquiryMessage={externalValues?.userContactInfoDependentInquiryMessage as MessageDescriptor | undefined}
    />
  )
}

const FeedbackStarsWidget = ({
  schema: { name = '', title, starLabels, testID, backgroundColor },
  options: { customStyles },
}: UICoreWidgetProps): ReactElement => (
  <Field
    name={name}
    label={title}
    starLabels={starLabels}
    periwinkle={backgroundColor === 'periwinkle'}
    testID={testID}
    component={FeedbackStarsRFF}
    customStyles={customStyles}
  />
)

type FilteredByFieldOptions = {
  filterByField?: string
  filterMapKey?: string
}

type FilteredByFieldFormContext = {
  externalValues?: Record<string, unknown>
}

type FilteredEnum = { enum: string[]; enumNames: string[] }

// Swap the enum based on a sibling field via externalValues[filterMapKey][triggerValue].
const FilteredByFieldCheckboxGroupWidget = (props: UICoreWidgetProps): ReactElement => {
  const { filterByField, filterMapKey } = (props.options as FilteredByFieldOptions | undefined) ?? {}
  const externalValues = (props.formContext as FilteredByFieldFormContext | undefined)?.externalValues
  const filterMap = filterMapKey
    ? (externalValues?.[filterMapKey] as Record<string, FilteredEnum> | undefined)
    : undefined
  const triggerField = useField(filterByField ?? '', { subscription: { value: true } })
  const triggerValue = triggerField.input.value

  const filtered = filterMap && triggerValue ? filterMap[triggerValue] : undefined

  // Clear selections when the trigger changes.
  const targetField = useField(props.schema.name ?? '', { subscription: { value: true } })
  const targetOnChange = targetField.input.onChange
  const previousTriggerValueRef = useRef(triggerValue)
  useEffect(() => {
    const previousValue = previousTriggerValueRef.current
    previousTriggerValueRef.current = triggerValue
    if (previousValue === triggerValue) return
    targetOnChange([])
  }, [triggerValue, targetOnChange])

  if (!filtered) {
    return <CheckboxGroupWidget {...props} />
  }

  return (
    <CheckboxGroupWidget {...props} schema={{ ...props.schema, enum: filtered.enum, enumNames: filtered.enumNames }} />
  )
}

const ShowAllHeaderRow = styled(View)(({ theme }) => ({
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '100%',
  marginBottom: theme.spacing['8px'],
}))

const ShowAllToggle = styled(Pressable)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing['4px'],
}))

const OptionsGrid = styled(View)({
  flexDirection: 'row',
  marginBottom: '80px',
})

const OptionsColumn = styled(View)(({ theme }) => ({
  flex: 1,
  paddingRight: theme.spacing['16px'],
}))

const OptionGridItem = styled(View)(({ theme }) => ({
  paddingVertical: theme.spacing['2px'],
  alignSelf: 'flex-start',
}))

/**
 * A typeAhead widget with a "Show all" toggle that reveals the full list of options in a
 * read-only, multi-column reference grid (display only — selection happens in the search box
 * above). Behaves identically to the standard `typeAhead` widget; the toggle and grid are
 * opt-in. Labels for the toggle can be customized via `widgetProps.showAllLabel` /
 * `widgetProps.hideAllLabel`.
 */
const TypeAheadWithShowAllWidget = (props: UICoreWidgetProps): ReactElement => {
  const { schema, formContext } = props
  const { name = '', title, options, widgetProps } = schema
  const { formatMessage } = useIntl()
  const { colors, breakpoints } = useTheme()
  const [showAll, setShowAll] = useState(false)

  const optionList: string[] = isArray(options)
    ? (options as string[])
    : isObject(options) && 'fromContext' in options && options.fromContext
    ? ((formContext as { [key: string]: unknown })[options.fromContext] as string[]) ?? []
    : []

  const showAllLabel =
    (widgetProps?.showAllLabel as string) ??
    formatMessage({ defaultMessage: 'Show all', description: 'Link to reveal the full list of options' })
  const hideAllLabel =
    (widgetProps?.hideAllLabel as string) ??
    formatMessage({ defaultMessage: 'Hide all', description: 'Link to collapse the full list of options' })

  // Lay the options out column-major (filling the first column top-to-bottom, then the next).
  const numColumns = breakpoints.isMobileSized ? 1 : 2
  const itemsPerColumn = Math.ceil(optionList.length / numColumns)
  const optionColumns = Array.from({ length: numColumns }, (_, columnIndex) =>
    optionList.slice(columnIndex * itemsPerColumn, columnIndex * itemsPerColumn + itemsPerColumn),
  )

  // Render our own header (title + toggle) so the inner typeAhead's label is suppressed,
  // and keep our custom props off the inner field.
  const { showAllLabel: _showAllLabel, hideAllLabel: _hideAllLabel, ...innerWidgetProps } = widgetProps ?? {}
  const innerSchema = { ...schema, title: undefined, widgetProps: innerWidgetProps }

  const innerOptions = showAll
    ? { ...props.options, baseInputStyle: { ...(props.options?.baseInputStyle as object), marginBottom: 4 } }
    : props.options

  return (
    <View>
      <ShowAllHeaderRow>
        {!!title && <BodyText size={BodyTextSize.DEFAULT} text={title} color={colors.textPrimary} />}
        <ShowAllToggle
          accessibilityRole='button'
          accessibilityState={{ expanded: showAll }}
          onPress={() => setShowAll((prev) => !prev)}
          testID={`${name}-show-all-toggle`}
        >
          <BodyText
            size={BodyTextSize.DEFAULT}
            text={showAll ? hideAllLabel : showAllLabel}
            color={colors.linkDefault}
          />
          <ChevronIcon
            size={16}
            fillColor={colors.linkDefault}
            direction={showAll ? ChevronIconDirection.UP : ChevronIconDirection.DOWN}
          />
        </ShowAllToggle>
      </ShowAllHeaderRow>

      <TypeAheadWidget {...props} schema={innerSchema} options={innerOptions} />

      {showAll && (
        <OptionsGrid testID={`${name}-all-options`}>
          {optionColumns.map((columnOptions, columnIndex) => (
            <OptionsColumn key={`${name}-column-${columnIndex}`}>
              {columnOptions.map((option) => (
                <OptionGridItem key={option}>
                  <BodyText size={BodyTextSize.SMALL} text={option} color={colors.textSecondary} />
                </OptionGridItem>
              ))}
            </OptionsColumn>
          ))}
        </OptionsGrid>
      )}
    </View>
  )
}

export default {
  TextWidget,
  CheckboxWidget,
  TextareaWidget,
  RangeWidget,
  DateWidget,
  time: TimeWidget,
  RadioWidget,
  SelectWidget,
  mask: MaskedInputWidget,
  checkboxGroup: CheckboxGroupWidget,
  filteredByFieldCheckboxGroup: FilteredByFieldCheckboxGroupWidget,
  typeAhead: TypeAheadWidget,
  typeAheadWithShowAll: TypeAheadWithShowAllWidget,
  multiSelect: MultiSelectWidget,
  internationalPhone: InternationalPhoneInputWidget,
  wordCloud: WordCloudWidget,
  imageRadio: ImageRadioWidget,
  inlineErrorBanner: InlineErrorBannerWidget,
  inlineSuccessBanner: InlineSuccessBannerWidget,
  inlineWarningBanner: InlineWarningBannerWidget,
  inlineInfoBanner: InlineInfoBannerWidget,
  countrySelector: CountrySelectorWidget,
  password: PasswordWidget,
  requiredCheckbox: RequiredCheckboxWidget,
  datePickerSelect: DatePickerSelectWidget,
  healthPlanSelector: HealthPlanSelectorWidget,
  buttonRange: ButtonRangeWidget,
  addIconWidget: AddIconWidget,
  editableInputField: EditableTextWidget,
  multicolorSlider: MulticolorSliderWidget,
  imageCheckbox: ImageCheckboxWidget,
  fileUploadWidget: FileWidget,
  GenAiHeader: GenAiHeaderWidget,
  userContactInformationCard: UserContactInformationCardWidget,
  radioV2: RadioWidgetV2,
  feedbackStars: FeedbackStarsWidget,
  dbtUnderageErrorBanner: DbtUnderageErrorBanner,
}
